import { useState } from 'react';
import type { MouseEvent, ReactNode, JSX } from 'react';
import { styled } from 'styled-components';
import {
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuGroup,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuItem,
} from '@radix-ui/react-dropdown-menu';
import { isNotNil } from '@wistia/type-guards';
import { useMq } from '../../hooks/useMq';
import { mq } from '../../helpers/mq';
import { Icon } from '../Icon';
import { menuContentCss } from './Menu';
import type { MenuItemButtonProps } from './MenuItemButton';
import { MenuItemButton } from './MenuItemButton';
import { MenuItemLabel } from './MenuItemLabel';
import { MenuItemDescription } from './MenuItemDescription';
import { useMenuContext } from './MenuContext';

export type SubMenuProps = {
  /**
   * The label of the submenu
   */
  label: ReactNode;
  /**
   * The description of the submenu
   */
  description?: ReactNode;
  /**
   * The menu items that will appear in this submenu
   */
  children: ReactNode[];
  /**
   * Callback when the submenu is opened or closed
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Icon to display on the left side of the SubMenu. Must be an instance of [Icon](?path=/docs/components-icon--docs)
   */
  icon?: JSX.Element | undefined;
};

const SubMenuContent = styled(DropdownMenuSubContent)<{ $compact: boolean }>`
  ${menuContentCss}

  ${mq.smAndDown} {
    transform: translateX(-100%) !important;
  }
`;

const StyledMobileSubMenuItems = styled.div`
  margin-left: var(--wui-space-04);
`;

const StyledSubTrigger = styled(DropdownMenuSubTrigger)`
  outline: none;

  &[data-state='open'],
  &[data-highlighted] {
    background-color: var(--wui-color-bg-surface-hover);
  }
`;

type SubMenuTriggerProps = MenuItemButtonProps;

const SubMenuTrigger = ({ icon, ...props }: SubMenuTriggerProps) => {
  const { isSmAndUp } = useMq();
  const Trigger = isSmAndUp ? StyledSubTrigger : DropdownMenuItem;
  return (
    <Trigger asChild={true}>
      <MenuItemButton
        {...props}
        leftIcon={icon}
        rightIcon={<Icon type="caret-right" />}
      />
    </Trigger>
  );
};

export const SubMenu = ({
  label,
  description,
  children,
  onOpenChange = () => null,
  ...props
}: SubMenuProps) => {
  const { isSmAndUp } = useMq();
  const [isExpanded, setIsExpanded] = useState(false);
  const { compact } = useMenuContext();

  return isSmAndUp ? (
    <DropdownMenuSub onOpenChange={onOpenChange}>
      <SubMenuTrigger {...(props as SubMenuTriggerProps)}>
        <MenuItemLabel>{label}</MenuItemLabel>
        {isNotNil(description) ? <MenuItemDescription>{description}</MenuItemDescription> : null}
      </SubMenuTrigger>

      <DropdownMenuPortal>
        <SubMenuContent $compact={compact}>{children}</SubMenuContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  ) : (
    <DropdownMenuGroup>
      <SubMenuTrigger
        {...(props as SubMenuTriggerProps)}
        onClick={(event: MouseEvent) => {
          event.preventDefault();
          setIsExpanded((prev) => !prev);
        }}
      >
        <MenuItemLabel>{label}</MenuItemLabel>
        <MenuItemDescription>{description}</MenuItemDescription>
      </SubMenuTrigger>

      <StyledMobileSubMenuItems>{isExpanded ? children : null}</StyledMobileSubMenuItems>
    </DropdownMenuGroup>
  );
};

SubMenu.displayName = 'SubMenu';
